import React, { FC, useMemo, useState } from 'react';
import { Button, Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import { DeliveryBadge, EmptyState, Notification, WarningCard } from 'common/components';
import { Bin } from 'common/svg';
import { ERoute } from 'common/const';
import { useAppSelector } from 'app/store/store.hooks';
import { getBasketGoodsDataSource, getBasketGoodsRowKeys, renderBasketRecords } from 'entities/Basket/Basket.helper';
import { IBasket, IDeleteBasketGoodPayload, IBasketGoods } from 'entities/Basket/Basket.models';
import { selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  basket: IBasket | null;
  goods: IBasketGoods[];
  selectedGoods: IBasketGoods[];
  loading: boolean;
  onCountChange: (id: number, value: number) => void;
  onSelectGood: (good: IBasketGoods, checked: boolean) => void;
  onDeleteGood: (payload: IDeleteBasketGoodPayload) => void;
}

export const BasketGoodsList: FC<IComponentProps> = ({
  basket,
  goods,
  selectedGoods,
  loading,
  onCountChange,
  onSelectGood,
  onDeleteGood,
}) => {
  const currentUser = useAppSelector(selectCurrentUser);

  const [goodId, setGoodId] = useState<number | null>(null);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const navigate = useNavigate();

  const dataSource = useMemo(() => getBasketGoodsDataSource(goods), [goods]);
  const goodsSelectedRowKeys = useMemo(() => getBasketGoodsRowKeys(selectedGoods), [selectedGoods]);
  const goodsExpandedRowKeys = useMemo(() => getBasketGoodsRowKeys(goods), [goods]);

  const openDeleteGoodWarningCard = (id: number): void => {
    setGoodId(id);
    setOpenWarningCard(true);
  };

  const closeDeleteGoodWarningCard = (): void => {
    setOpenWarningCard(false);
  };

  const deleteGood = (): void => {
    onDeleteGood({
      id: goodId,
      onSuccess: () => setOpenWarningCard(false),
    });
  };

  const navigateToCatalog = (): void => {
    navigate(ERoute.Catalog);
  };

  return (
    <div className="relative h-full overflow-hidden">
      <div className="absolute w-full h-full overflow-y-scroll scrollbar-none" style={{ paddingBottom: 149 }}>
        {!!goods.length && (
          <Table
            className="table-expandable basket__goods-list"
            rowClassName="expanded"
            dataSource={dataSource}
            columns={renderBasketRecords({ account: currentUser?.account, onCountChange: onCountChange })}
            rowSelection={{
              selectedRowKeys: goodsSelectedRowKeys,
              onSelect: onSelectGood,
              columnWidth: 20,
              getCheckboxProps: (record) => ({
                disabled: !record.isAvailableForCustomer
                  ? true
                  : basket?.maxGoodPrice !== undefined
                    ? basket.maxGoodPrice < record.price
                    : false,
              }),
            }}
            expandable={{
              showExpandColumn: false,
              expandedRowKeys: goodsExpandedRowKeys,
              expandedRowRender: (record) => {
                return (
                  <>
                    {basket?.maxGoodPrice !== undefined && basket.maxGoodPrice < record.price && (
                      <div className="mb-9">
                        <Notification type="error" description="Стоимость позиции превышает допустимый лимит на цену товара." />
                      </div>
                    )}

                    {!record.isAvailableForCustomer && (
                      <div className="mb-9">
                        <Notification type="error" description="Товар недоступен для заказа" />
                      </div>
                    )}

                    <div className="flex align-center justify-between">
                      <DeliveryBadge remains={record.remains} count={record.count} />

                      <Button className="button-text dark-green" onClick={() => openDeleteGoodWarningCard(record.goodId)}>
                        <span>Удалить товар</span>

                        <Bin className="icon-bin-dark-green" />
                      </Button>
                    </div>
                  </>
                );
              },
            }}
            pagination={false}
            showHeader={false}
          />
        )}
      </div>

      {!basket?.goods.length && !loading && (
        <div className="h-full flex align-center justify-center">
          <EmptyState
            title="Корзина пуста"
            description="Воспользуйтесь каталогом, чтобы найти нужные вам товары."
            btnTitle="Открыть каталог"
            onClick={navigateToCatalog}
          />
        </div>
      )}

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите удалить товар?"
        subtitle="Отменить данное действие будет невозможно."
        confirmBtnTitle="Удалить"
        loading={loading}
        onClose={closeDeleteGoodWarningCard}
        onConfirm={deleteGood}
      />
    </div>
  );
};
